import React, { useState, useEffect } from 'react';
import { FaUserShield, FaUserCheck, FaUserTimes, FaHourglassHalf, FaSearch, FaHistory, FaCheckCircle, FaClipboardList } from 'react-icons/fa';
import Toast from '../components/Toast';
import * as adminService from '../services/adminService';

const AdminSubEBManagement = () => {
    const [requests, setRequests] = useState([]);
    const [verifiedUsers, setVerifiedUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [toast, setToast] = useState(null);
    const [actionId, setActionId] = useState(null);
    const [activeTab, setActiveTab] = useState('requests'); // 'requests' or 'verified'

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [requestsRes, verifiedRes] = await Promise.all([
                adminService.getSubEBRequests(),
                adminService.getVerifiedSubEBUsers()
            ]);

            // Process requests (From Postman: returns { success: true, data: [...] })
            const reqResponse = requestsRes.data || requestsRes;
            const requestsList = Array.isArray(reqResponse) ? reqResponse : (reqResponse.data || []);
            setRequests(requestsList);

            // Process verified users
            const verResponse = verifiedRes.data || verifiedRes;
            const verifiedList = Array.isArray(verResponse) ? verResponse : (verResponse.data || []);
            setVerifiedUsers(verifiedList);

        } catch (error) {
            console.error('Error loading Sub-EB data:', error);
            showToast('Failed to load Sub-EB data', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleVerify = async (targetUserId) => {
        setActionId(targetUserId);
        try {
            await adminService.verifySubEB(targetUserId);
            showToast('User verified and promoted to Editorial Board successfully.', 'success');
            loadData();
        } catch (error) {
            console.error('Error verifying Sub-EB:', error);
            showToast(error.message || 'Failed to verify request', 'error');
        } finally {
            setActionId(null);
        }
    };

    const handleRevoke = async (userId) => {
        setActionId(userId);
        try {
            await adminService.revokeSubEB(userId);
            showToast('Editorial Board rights revoked successfully.', 'success');
            loadData();
        } catch (error) {
            console.error('Error revoking Sub-EB:', error);
            showToast(error.message || 'Failed to revoke rights', 'error');
        } finally {
            setActionId(null);
        }
    };

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };

    const filteredRequests = requests.filter((req) => {
        // According to Postman, 'req' is the user object, and 'supervisor' is the recommender
        const user = req;
        const supervisor = req.supervisor || {};
        const search = searchTerm.toLowerCase();

        return (user.full_name || '').toLowerCase().includes(search) ||
            (user.email || '').toLowerCase().includes(search) ||
            (supervisor.full_name || '').toLowerCase().includes(search);
    });

    const filteredVerified = verifiedUsers.filter((user) => {
        const search = searchTerm.toLowerCase();
        const supervisor = user.supervisor || {};
        return (user.full_name || '').toLowerCase().includes(search) ||
            (user.email || '').toLowerCase().includes(search) ||
            (supervisor.full_name || '').toLowerCase().includes(search);
    });

    const darkGradient = {
        background: 'linear-gradient(135deg, rgb(15, 23, 42) 0%, rgb(49, 46, 129) 50%, rgb(15, 23, 42) 100%)'
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Loading Sub-EB members...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4">
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}

            <div className="max-w mx-auto">
                {/* Header */}
                <div className="rounded-2xl shadow-lg p-8 mb-8" style={darkGradient}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                                <FaUserShield className="w-8 h-8 text-white" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-white">Sub-EB Management</h1>
                                <p className="text-blue-100 mt-1">Review and manage Editorial Board promotion requests</p>
                            </div>
                        </div>
                        <div className="flex gap-8">
                            <div className="text-right">
                                <p className="text-blue-200 text-sm font-medium">Pending</p>
                                <p className="text-3xl font-bold text-white tracking-tight">{requests.length}</p>
                            </div>
                            <div className="text-right border-l border-white/10 pl-8">
                                <p className="text-blue-200 text-sm font-medium">Verified</p>
                                <p className="text-3xl font-bold text-white tracking-tight">{verifiedUsers.length}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs & Search */}
                <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
                    <div className="flex flex-col md:flex-row gap-6 items-center">
                        <div className="flex p-1 bg-gray-100 rounded-xl">
                            <button
                                onClick={() => setActiveTab('requests')}
                                className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-bold transition-all ${activeTab === 'requests'
                                    ? 'bg-white text-blue-600 shadow-md'
                                    : 'text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                <FaClipboardList /> Requests
                            </button>
                            <button
                                onClick={() => setActiveTab('verified')}
                                className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-bold transition-all ${activeTab === 'verified'
                                    ? 'bg-white text-blue-600 shadow-md'
                                    : 'text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                <FaCheckCircle /> Verified Members
                            </button>
                        </div>
                        <div className="relative flex-1 w-full">
                            <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder={activeTab === 'requests' ? "Search requests..." : "Search verified members..."}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all outline-none"
                            />
                        </div>
                        <button
                            onClick={loadData}
                            className="flex items-center gap-2 px-6 py-3 bg-blue-50 text-blue-600 rounded-xl font-bold hover:bg-blue-100 transition-all border border-blue-200 h-full"
                        >
                            <FaHistory /> Refresh
                        </button>
                    </div>
                </div>

                {/* Content Table */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50/50 border-bottom border-gray-100">
                                    <th className="px-6 py-5 text-sm font-bold text-gray-900 uppercase tracking-wider">User Details</th>
                                    <th className="px-6 py-5 text-sm font-bold text-gray-900 uppercase tracking-wider">
                                        Recommended By
                                    </th>
                                    <th className="px-6 py-5 text-sm font-bold text-gray-900 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-5 text-sm font-bold text-gray-900 uppercase tracking-wider text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {activeTab === 'requests' ? (
                                    filteredRequests.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="px-6 py-16 text-center text-gray-500">
                                                <div className="flex flex-col items-center gap-2">
                                                    <FaClipboardList className="w-12 h-12 text-gray-100 mb-2" />
                                                    <p className="text-lg font-semibold">No pending requests</p>
                                                    <p className="text-sm">New recommendations will appear here.</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredRequests.map((user) => {
                                            const supervisor = user.supervisor || {};
                                            const isVerified = user.is_verified_sub_eb === true;
                                            const currentActionId = actionId === user.id;

                                            return (
                                                <tr key={user.id} className="hover:bg-blue-50/30 transition-colors group">
                                                    <td className="px-6 py-6">
                                                        <div className="flex items-center gap-4">
                                                            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-lg">
                                                                {user.full_name ? user.full_name.charAt(0).toUpperCase() : '?'}
                                                            </div>
                                                            <div>
                                                                <div className="font-bold text-gray-900">{user.full_name || 'Anonymous'}</div>
                                                                <div className="text-sm text-gray-500">{user.email}</div>
                                                                {user.phone_number && (
                                                                    <div className="text-xs text-blue-600 font-medium mt-0.5">{user.phone_number}</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-6">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-xs">
                                                                {supervisor.full_name ? supervisor.full_name.charAt(0).toUpperCase() : '?'}
                                                            </div>
                                                            <div className="text-sm">
                                                                <div className="font-semibold text-gray-800">{supervisor.full_name || 'EB Member'}</div>
                                                                <div className="text-xs text-gray-400">{supervisor.email || 'N/A'}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-6">
                                                        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${isVerified
                                                            ? 'bg-green-100 text-green-700'
                                                            : 'bg-yellow-100 text-yellow-700'
                                                            }`}>
                                                            {isVerified ? 'Verified' : 'Pending'}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-6 text-right">
                                                        <div className="flex justify-end gap-2">
                                                            {!isVerified ? (
                                                                <button
                                                                    onClick={() => handleVerify(user.id)}
                                                                    disabled={currentActionId}
                                                                    className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-bold hover:bg-green-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50"
                                                                >
                                                                    {currentActionId ? <FaHourglassHalf className="animate-spin" /> : <FaUserCheck />}
                                                                    Verify
                                                                </button>
                                                            ) : (
                                                                <span className="text-green-600 font-bold text-sm flex items-center gap-1">
                                                                    <FaCheckCircle /> Ready
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )
                                ) : (
                                    filteredVerified.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="px-6 py-16 text-center text-gray-500">
                                                <div className="flex flex-col items-center gap-2">
                                                    <FaCheckCircle className="w-12 h-12 text-gray-100 mb-2" />
                                                    <p className="text-lg font-semibold">No verified members yet</p>
                                                    <p className="text-sm">Verify requests to add them here.</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredVerified.map((user) => {
                                            const supervisor = user.supervisor || {};
                                            const currentActionId = actionId === user.id;

                                            return (
                                                <tr key={user.id} className="hover:bg-blue-50/30 transition-colors group">
                                                    <td className="px-6 py-6">
                                                        <div className="flex items-center gap-4">
                                                            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold text-lg">
                                                                {user.full_name ? user.full_name.charAt(0).toUpperCase() : '?'}
                                                            </div>
                                                            <div>
                                                                <div className="font-bold text-gray-900">{user.full_name || 'Anonymous'}</div>
                                                                <div className="text-sm text-gray-500">{user.email}</div>
                                                                {user.phone_number && (
                                                                    <div className="text-xs text-blue-600 font-medium mt-0.5">{user.phone_number}</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-6">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-xs">
                                                                {supervisor.full_name ? supervisor.full_name.charAt(0).toUpperCase() : '?'}
                                                            </div>
                                                            <div className="text-sm">
                                                                <div className="font-semibold text-gray-800">{supervisor.full_name || 'Admin/EB'}</div>
                                                                <div className="text-xs text-gray-400">{supervisor.email || 'N/A'}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-6">
                                                        <span className="px-3 py-1 rounded-full text-xs font-bold uppercase bg-green-100 text-green-700">
                                                            EB Member
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-6 text-right">
                                                        <button
                                                            onClick={() => handleRevoke(user.id)}
                                                            disabled={currentActionId}
                                                            className="flex items-center gap-1.5 px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg text-sm font-bold hover:bg-red-100 transition-all disabled:opacity-50 ml-auto"
                                                        >
                                                            {currentActionId ? <FaHourglassHalf className="animate-spin" /> : <FaUserTimes />}
                                                            Revoke Rights
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminSubEBManagement;
